import map from 'lodash/map';
import { QueryHookOptions } from '@apollo/client';
import React from 'react';

import {
  ChainFilter,
  ChainFragment,
  ChainsQuery,
  ChainsQueryVariables,
  useChainsQuery,
} from '@occc/graphql';
import { Chain } from '@occc-app/Chain/types';

import { RollbackStackingConfig } from './types';
import { isRollbackStackingConfigValid } from './validation';
import { fallbackRollbackStackingConfig } from './constants';

const transformDataToChainsArray = (data: ChainsQuery): ChainFragment[] => {
  const { edges } = data.chains;
  return map(edges, 'node');
};

const decorateChain = (chain: ChainFragment, organizationId?: string | null): Chain => {
  const rollbackStackingConfig: RollbackStackingConfig = isRollbackStackingConfigValid(
    chain.rollbackStackingConfig,
  )
    ? chain.rollbackStackingConfig
    : fallbackRollbackStackingConfig;

  return {
    id: chain.id,
    configuration: {
      manualPointChangeCap: chain.configuration.manualPointChangeCap ?? null,
      allowedPointAdjustmentReasons: chain.configuration.allowedPointAdjustmentReasons,
      referralProgramConfigurationEnabled: chain.configuration.referralProgramConfigurationEnabled,
      referralProgramConfiguration: chain.configuration.referralProgramConfiguration ?? null,
      offerReturnStrategy: chain.configuration.offerReturnStrategy,
      offerReturnDefaultStrategy: chain.configuration.offerReturnDefaultStrategy,
      pointExpiration: chain.configuration.pointExpiration,
      allowMultipleTransactionDiscounts:
        chain.configuration.allowMultipleTransactionDiscounts ?? false,
    },
    attributes: {
      created_at: null,
      deleted_at: null,
      location_ids: [],
      name: chain.name,
      organization_id: organizationId,
      updated_at: null,
      rollbackStackingConfig,
      isFuelRollbackVoucherStackingEnabled: chain.rollbackVoucherStackingEnabled,
    },
  };
};

const transformChainsArray = (
  data: ChainsQuery,
  organizationId: string | null | undefined,
): Chain[] => {
  const array = transformDataToChainsArray(data);
  const decoratedArray: Chain[] = [];
  for (const ch of array) {
    const item = decorateChain(ch, organizationId);
    decoratedArray.push(item);
  }
  return decoratedArray;
};

export const useGetChains = (
  filter: ChainFilter,
  baseOptions?: QueryHookOptions<ChainFragment[], ChainsQueryVariables>,
) => {
  const response = useChainsQuery({
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    variables: {
      filter,
    },
    ...baseOptions,
    onCompleted: (data) => {
      if (baseOptions && baseOptions.onCompleted) {
        baseOptions.onCompleted(transformDataToChainsArray(data));
      }
    },
  } as QueryHookOptions);

  const data = React.useMemo(() => {
    if (response.data) {
      return transformChainsArray(response.data, filter.organizationIdEquals);
    }
    return response.data;
  }, [filter.organizationIdEquals, response.data]);

  return {
    ...response,
    data,
  };
};
